'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppStore } from '@/store/app';
import { api } from '@/lib/api';

interface EconomicEvent {
  id: number;
  title: string;
  country: string;
  importance: string;
  date: string;
  actual: string | null;
  forecast: string | null;
  previous: string | null;
  status: string;
}

const COUNTRY_FLAG: Record<string, string> = {
  USD: '🇺🇸', EUR: '🇪🇺', GBP: '🇬🇧', JPY: '🇯🇵', CNY: '🇨🇳', AUD: '🇦🇺',
  CAD: '🇨🇦', CHF: '🇨🇭', NZD: '🇳🇿', HKD: '🇭🇰', SGD: '🇸🇬', KRW: '🇰🇷',
  INR: '🇮🇳', BRL: '🇧🇷', MXN: '🇲🇽', RUB: '🇷🇺', TRY: '🇹🇷', ZAR: '🇿🇦',
  SEK: '🇸🇪', NOK: '🇳🇴', DKK: '🇩🇰', PLN: '🇵🇱', IDR: '🇮🇩', THB: '🇹🇭',
};

const IMPORTANCE_STYLE: Record<string, { badge: string; dot: string }> = {
  High: { badge: 'bg-gf-negative/15 text-gf-negative border-gf-negative/30', dot: 'bg-gf-negative' },
  Medium: { badge: 'bg-gf-warn/15 text-gf-warn border-gf-warn/30', dot: 'bg-gf-warn' },
  Low: { badge: 'bg-gf-surface2 text-gf-muted border-gf-border', dot: 'bg-gf-muted' },
};

const COUNTRY_ZH: Record<string, string> = {
  'Euro Zone': '欧元区', German: '德国', French: '法国', Italian: '意大利',
  Spanish: '西班牙', Japanese: '日本', Chinese: '中国', Australian: '澳洲',
  Canadian: '加拿大', Swiss: '瑞士', UK: '英国', Dutch: '荷兰', Belgian: '比利时',
  Austrian: '奥地利', 'New Zealand': '新西兰', Korean: '韩国', Brazilian: '巴西',
  Mexican: '墨西哥', Indian: '印度', Russian: '俄罗斯', Turkish: '土耳其',
  Polish: '波兰', Swedish: '瑞典', Norwegian: '挪威', Danish: '丹麦',
  Singaporean: '新加坡', 'South African': '南非', US: '美国', BoJ: '日本央行',
  BoE: '英国央行', ECB: '欧洲央行', RBA: '澳洲央行', BoC: '加拿大央行',
  SNB: '瑞士央行', RBNZ: '新西兰央行',
};

const EVENT_ZH_PATTERNS: [RegExp, string][] = [
  // R11: full-phrase rules first — never assemble a hybrid like "Foreign Ex 变动"
  [/Inflation Rate/i, '通胀率'],
  [/GDP Growth Rate QoQ/i, 'GDP 增长率 季率'],
  [/GDP Growth Rate YoY/i, 'GDP 增长率 年率'],
  [/GDP Growth Rate/i, 'GDP 增长率'],
  [/Continuing Jobless Claims/i, '续请失业金'],
  [/Continuing Claims/i, '续请失业金'],
  [/Continuing/i, '续请'],
  [/Passenger Vehicles Sales/i, '乘用车销售'],
  [/Total Vehicle Sales/i, '汽车总销量'],
  [/Foreign Exchange Reserves/i, '外汇储备'],
  [/Foreign Exchange/i, '外汇'],
  [/Reserves/i, '储备'],
  [/Total Vehicle Sales/i, '汽车总销量'],
  [/Vehicle Sales/i, '汽车销量'],
  [/Nonfarm Productivity/i, '非农生产率'],
  [/Unit Labor Costs/i, '单位劳动成本'],
  [/Leading Index/i, '领先指数'],
  [/Coincident Index/i, '同步指数'],
  [/Consumer Credit/i, '消费者信贷'],
  [/Natural Gas Storage/i, '天然气库存'],
  [/Mortgage/i, '抵押贷款'],
  [/10-Year Note Auction/i, '10 年期国债拍卖'],
  [/5-Year Note Auction/i, '5 年期国债拍卖'],
  [/2-Year Note Auction/i, '2 年期国债拍卖'],
  [/30-Year Bond Auction/i, '30 年期国债拍卖'],
  [/FOMC Statement/i, 'FOMC 声明'],
  [/FOMC Press Conference/i, 'FOMC 新闻发布会'],
  [/FOMC Member/i, 'FOMC 委员讲话'],
  [/FOMC/i, 'FOMC'],
  [/Monetary Policy Meeting Minutes/i, '货币政策会议纪要'],
  [/Meeting Minutes/i, '会议纪要'],
  [/Interest Rate Decision/i, '利率决议'],
  [/Federal Funds Rate/i, '联邦基金利率'],
  [/Core PCE Price Index/i, '核心 PCE 物价指数'],
  [/PCE Price Index/i, 'PCE 物价指数'],
  [/Core CPI/i, '核心 CPI'],
  [/CPI/i, 'CPI'],
  [/PPI/i, 'PPI'],
  [/Non-Farm Employment Change/i, '非农就业变动'],
  [/Nonfarm Payrolls/i, '非农就业'],
  [/Non-Farm Payrolls/i, '非农就业'],
  [/ADP Nonfarm Employment Change/i, 'ADP 非农就业变动'],
  [/ADP Employment Change/i, 'ADP 就业变动'],
  [/ADP/i, 'ADP'],
  [/Unemployment Rate/i, '失业率'],
  [/Unemployment Change/i, '失业变动'],
  [/Unemployment/i, '失业'],
  [/Average Hourly Earnings/i, '平均时薪'],
  [/Initial Jobless Claims/i, '初请失业金'],
  [/Jobless Claims/i, '失业救济申请'],
  [/ISM Manufacturing/i, 'ISM 制造业'],
  [/ISM Services/i, 'ISM 服务业'],
  [/Retail Sales/i, '零售销售'],
  [/Goods Trade/i, '商品贸易'],
  [/Goods/i, '商品'],
  [/Trade Balance/i, '贸易收支'],
  [/Industrial Production/i, '工业生产'],
  [/Capacity Utilization/i, '产能利用率'],
  [/Consumer Confidence/i, '消费者信心'],
  [/Michigan Sentiment/i, '密歇根消费者信心'],
  [/Michigan/i, '密歇根指数'],
  [/Durable Goods Orders/i, '耐用品订单'],
  [/Housing Starts/i, '新屋开工'],
  [/Building Permits/i, '营建许可'],
  [/Existing Home Sales/i, '成屋销售'],
  [/New Home Sales/i, '新屋销售'],
  [/Current Account/i, '经常帐'],
  [/Business Inventories/i, '商业库存'],
  [/Wholesale Inventories/i, '批发库存'],
  [/Crude Oil Inventories/i, '原油库存'],
  [/Crude Oil/i, '原油'],
  [/Inventories/i, '库存'],
  [/Philadelphia Fed/i, '费城联储'],
  [/Empire State/i, '纽约联储'],
  [/Chicago PMI/i, '芝加哥 PMI'],
  [/Dallas Fed/i, '达拉斯联储'],
  [/Richmond Fed/i, '里士满联储'],
  [/Kansas City Fed/i, '堪萨斯城联储'],
  [/Producer Price Index/i, '生产者物价指数(PPI)'],
  [/GDP Price Index/i, 'GDP 物价指数'],
  [/GDP Price/i, 'GDP 物价'],
  [/GDP/i, 'GDP'],
  [/GNP/i, 'GNP'],
  [/PMI/i, 'PMI'],
  [/FPC/i, '金融政策委员会'],
  [/MPC/i, '货币政策委员会'],
  [/Gov Board Member/i, '央行委员会委员'],
  [/Board Member/i, '委员会委员'],
  [/Member/i, '委员'],
  [/Personal Income/i, '个人收入'],
  [/Personal Spending/i, '个人支出'],
  [/Personal Consumption/i, '个人消费'],
  [/Quarterly Bulletin/i, '季度报告'],
  [/Economic Expectations/i, '经济预期'],
  [/Expectations/i, '预期'],
  [/Prelim/i, '初值'],
  [/Flash/i, '初值'],
  [/Advance/i, '初值'],
  [/Final/i, '终值'],
  [/Bank Holiday/i, '银行假日'],
  [/Holiday/i, '假日'],
  [/Budget/i, '预算'],
  [/Bubill/i, '国库券'],
  [/Readout/i, '报告'],
  [/Bank Lending/i, '银行贷款'],
  [/Bank/i, '银行'],
  [/Westpac/i, '西太平洋银行'],
  [/BCB/i, '巴西央行'],
  [/NODX/i, '非石油出口'],
  [/Exports/i, '出口'],
  [/Imports/i, '进口'],
  [/WPI/i, '批发物价'],
  [/QoQ/i, '季率'],
  [/m\/m/i, '月率'],
  [/Speech/i, '讲话'],
  [/Speaks/i, '讲话'],
  [/Speaker/i, '讲话'],
  [/Auction/i, '拍卖'],
  [/Bond/i, '债券'],
  [/Price Index/i, '物价指数'],
  [/Price/i, '物价'],
  [/Index/i, '指数'],
  [/Balance/i, '收支'],
  [/Sentiment/i, '信心'],
  [/Employment/i, '就业'],
  [/Change/i, '变动'],
  [/m\/m/i, '月率'],
  [/q\/q/i, '季率'],
  [/y\/y/i, '年率'],
  [/YoY/i, '年率'],
  [/MoM/i, '月率'],
  [/Adv/i, '初值'],
  [/s\.a/i, '季节调整'],
  [/SA\b/i, '季节调整'],
  [/NSA\b/i, '未季调'],
];

function translateEventTitle(title: string): string {
  // R11: normalise non-breaking spaces first — tradingeconomics/forexfactory
  // titles use \u00A0, which would otherwise defeat every plain-space regex.
  let t = title.replace(/\u00a0/g, ' ');
  for (const [en, zh] of Object.entries(COUNTRY_ZH)) {
    if (t.includes(en)) {
      t = t.replace(en, zh);
      break;
    }
  }
  for (const [re, zh] of EVENT_ZH_PATTERNS) {
    t = t.replace(re, zh);
  }
  return t.replace(/\s+/g, ' ').trim();
}

export default function EconomicCalendar() {
  const { language } = useAppStore();
  const isZh = language !== 'en';
  const [filter, setFilter] = useState<'all' | 'high'>('all');
  const [tab, setTab] = useState<'upcoming' | 'ended'>('upcoming');
  // D17: date range / country / impact / keyword filters (client-side on the fetched dataset).
  const [dateRange, setDateRange] = useState<'all' | 'today' | 'week' | '7d'>('all');
  const [countryFilter, setCountryFilter] = useState<string>('all');
  const [impactFilter, setImpactFilter] = useState<'all' | 'High' | 'Medium' | 'Low'>('all');
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['economic-calendar'],
    queryFn: () => api.get<{ upcoming: EconomicEvent[]; ended: EconomicEvent[]; source: string }>('/market/calendar/economic'),
    refetchInterval: 30 * 60_000,
    staleTime: 60_000,
  });

  // D17: apply the local filter set to a bucket.
  const applyFilters = (list: EconomicEvent[]): EconomicEvent[] => {
    let out = list;
    if (filter === 'high') out = out.filter(e => e.importance === 'High');
    if (impactFilter !== 'all') out = out.filter(e => e.importance === impactFilter);
    if (countryFilter !== 'all') out = out.filter(e => e.country === countryFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter(e => e.title.toLowerCase().includes(q) || e.country.toLowerCase().includes(q));
    }
    const now = new Date();
    const sod = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const weekStart = sod - now.getDay() * 86400000;
    if (dateRange === 'today') out = out.filter(e => new Date(e.date).getTime() >= sod);
    else if (dateRange === 'week') out = out.filter(e => new Date(e.date).getTime() >= weekStart);
    else if (dateRange === '7d') out = out.filter(e => new Date(e.date).getTime() >= now.getTime() - 7 * 86400000);
    return out;
  };

  const upcoming = applyFilters(data?.upcoming || []);
  const ended = applyFilters(data?.ended || []);

  // D17: distinct countries for the filter dropdown.
  const countries = Array.from(new Set([...(data?.upcoming || []), ...(data?.ended || [])].map(e => e.country))).sort();

  const t = {
    title: isZh ? '宏观经济日历' : 'Economic Calendar',
    all: isZh ? '全部' : 'All',
    highImpact: isZh ? '高影响' : 'High Impact',
    upcoming: isZh ? '即将到来' : 'Upcoming',
    ended: isZh ? '已结束' : 'Ended',
    time: isZh ? '时间' : 'Time',
    event: isZh ? '事件' : 'Event',
    country: isZh ? '国家' : 'Country',
    importance: isZh ? '重要度' : 'Impact',
    previous: isZh ? '前值' : 'Previous',
    forecast: isZh ? '预期' : 'Forecast',
    actual: isZh ? '实际' : 'Actual',
    loading: isZh ? '加载中...' : 'Loading...',
    noData: isZh ? '暂无数据' : 'No data',
    source: isZh ? '数据来源：ForexFactory（实际值以官方公布为准）' : 'Source: ForexFactory (actual pending official release)',
  };

  const fmtTime = (iso: string) => {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getMonth() + 1}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const timezoneLabel = (() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const off = -new Date().getTimezoneOffset() / 60;
      const sign = off >= 0 ? '+' : '-';
      return `${tz} (UTC${sign}${Math.abs(off)})`;
    } catch {
      return 'UTC';
    }
  })();

  const renderImportance = (imp: string) => {
    const s = IMPORTANCE_STYLE[imp] || IMPORTANCE_STYLE.Low;
    const label = isZh
      ? imp === 'High' ? '高' : imp === 'Medium' ? '中' : '低'
      : imp === 'High' ? 'High' : imp === 'Medium' ? 'Med' : 'Low';
    return (
      <span className={`inline-flex items-center gap-1.5 rounded border px-1.5 py-0.5 text-xs font-medium ${s.badge}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
        {label}
      </span>
    );
  };

  const title = (e: EconomicEvent) => (isZh ? translateEventTitle(e.title) : e.title);
  const value = (v: string | null) => (v != null && v !== '' ? v : '—');
  // R11: country names follow the same language as the UI; fall back to the original when unmapped.
  const countryLabel = (c: string) => (isZh ? COUNTRY_ZH[c] || c : c);

  // D17: released deviation = actual − forecast, shown factually without implying a bullish/bearish read.
  const releasedDiff = (e: EconomicEvent): { label: string; dir: 'up' | 'down' | 'flat' } | null => {
    if (e.actual == null || e.actual === '' || e.forecast == null || e.forecast === '') return null;
    const a = parseFloat(String(e.actual).replace(/[^\d.\-]/g, ''));
    const f = parseFloat(String(e.forecast).replace(/[^\d.\-]/g, ''));
    if (!Number.isFinite(a) || !Number.isFinite(f)) return null;
    const d = a - f;
    if (Math.abs(d) < 1e-9) return { label: '0.00', dir: 'flat' };
    return { label: `${d > 0 ? '+' : '-'}${Math.abs(d).toFixed(2)}`, dir: d > 0 ? 'up' : 'down' };
  };

  const renderTable = (list: EconomicEvent[], isEnded: boolean) => (
    <div className="card hidden overflow-hidden sm:block">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="text-left text-xs text-gf-muted">
              <th className="whitespace-nowrap px-3 py-2 font-medium">{t.time}</th>
              <th className="px-3 py-2 font-medium">{t.event}</th>
              <th className="whitespace-nowrap px-3 py-2 font-medium">{t.country}</th>
              <th className="whitespace-nowrap px-3 py-2 font-medium">{t.importance}</th>
              <th className="whitespace-nowrap px-3 py-2 font-medium">{t.previous}</th>
              <th className="whitespace-nowrap px-3 py-2 font-medium">{t.forecast}</th>
              <th className="whitespace-nowrap px-3 py-2 font-medium">{t.actual}</th>
              <th className="whitespace-nowrap px-3 py-2 font-medium">{isZh ? '公布偏差' : 'Deviation'}</th>
            </tr>
          </thead>
          <tbody>
            {list.map(e => {
              const diff = releasedDiff(e);
              return (
                <tr key={e.id} className={`border-t border-gf-border text-sm ${isEnded ? 'opacity-55' : ''}`}>
                  <td className="whitespace-nowrap py-2.5 pr-3 font-mono text-xs text-gf-muted">{fmtTime(e.date)}</td>
                  <td className="py-2.5 pr-3 font-medium text-gf-text">{title(e)}</td>
                  <td className="whitespace-nowrap py-2.5 pr-3">
                    <span className="text-sm">{COUNTRY_FLAG[e.country] || '🌐'}</span>
                    <span className="ml-1.5 text-xs text-gf-muted">{countryLabel(e.country)}</span>
                  </td>
                  <td className="whitespace-nowrap py-2.5 pr-3">{renderImportance(e.importance)}</td>
                  <td className="whitespace-nowrap py-2.5 pr-3 text-xs text-gf-muted">{value(e.previous)}</td>
                  <td className="whitespace-nowrap py-2.5 pr-3 text-xs text-gf-accent">{value(e.forecast)}</td>
                  <td className="whitespace-nowrap py-2.5 text-xs text-gf-positive">{value(e.actual)}</td>
                  <td className="whitespace-nowrap py-2.5 pl-3 text-right text-xs">
                    {diff ? (
                      <span className={diff.dir === 'up' ? 'text-gf-positive' : diff.dir === 'down' ? 'text-gf-negative' : 'text-gf-muted'}>
                        {diff.label}
                      </span>
                    ) : (
                      <span className="text-gf-muted">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderCards = (list: EconomicEvent[], isEnded: boolean) => (
    <div className="space-y-2 sm:hidden">
      {list.map(e => {
        const diff = releasedDiff(e);
        return (
          <div key={e.id} className={`card p-3 ${isEnded ? 'opacity-55' : ''}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-semibold leading-tight">{title(e)}</div>
                <div className="mt-1 flex items-center gap-2 text-xs text-gf-muted">
                  <span>{COUNTRY_FLAG[e.country] || '🌐'} {countryLabel(e.country)}</span>
                  <span className="font-mono">{fmtTime(e.date)}</span>
                </div>
              </div>
              {renderImportance(e.importance)}
            </div>
            <div className="mt-2 grid grid-cols-4 gap-2 text-center text-xs">
              <div className="rounded-lg bg-gf-surface2 p-2">
                <div className="text-gf-muted">{t.previous}</div>
                <div className="mt-0.5 font-medium text-gf-text">{value(e.previous)}</div>
              </div>
              <div className="rounded-lg bg-gf-surface2 p-2">
                <div className="text-gf-muted">{t.forecast}</div>
                <div className="mt-0.5 font-medium text-gf-accent">{value(e.forecast)}</div>
              </div>
              <div className="rounded-lg bg-gf-surface2 p-2">
                <div className="text-gf-muted">{t.actual}</div>
                <div className="mt-0.5 font-medium text-gf-positive">{value(e.actual)}</div>
              </div>
              <div className="rounded-lg bg-gf-surface2 p-2">
                <div className="text-gf-muted">{isZh ? '偏差' : 'Dev'}</div>
                <div className={`mt-0.5 font-medium ${diff ? (diff.dir === 'up' ? 'text-gf-positive' : diff.dir === 'down' ? 'text-gf-negative' : 'text-gf-muted') : 'text-gf-muted'}`}>
                  {diff ? diff.label : '—'}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{t.title}</h2>
        <span className="text-[10px] text-gf-muted">{isZh ? '时区' : 'TZ'}: {timezoneLabel}</span>
        <div className="flex items-center gap-1.5">          <button
            onClick={() => setFilter('all')}
            className={`rounded-full border px-3 py-1 text-xs ${filter === 'all' ? 'border-gf-accent bg-gf-accent/15 text-gf-accent' : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text'}`}
          >
            {t.all}
          </button>
          <button
            onClick={() => setFilter('high')}
            className={`rounded-full border px-3 py-1 text-xs ${filter === 'high' ? 'border-gf-accent bg-gf-accent/15 text-gf-accent' : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text'}`}
          >
            {t.highImpact}
          </button>
        </div>
      </div>

      {/* D17: date range / country / impact / keyword filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value as typeof dateRange)}
          className="input !h-8 !w-auto !px-2"
        >
          <option value="all">{isZh ? '全部时间' : 'All time'}</option>
          <option value="today">{isZh ? '今日' : 'Today'}</option>
          <option value="week">{isZh ? '本周' : 'This week'}</option>
          <option value="7d">{isZh ? '近 7 天' : 'Last 7 days'}</option>
        </select>
        <select
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value)}
          className="input !h-8 !w-auto !px-2"
        >
          <option value="all">{isZh ? '全部国家' : 'All countries'}</option>
          {countries.map((c) => (
            <option key={c} value={c}>{countryLabel(c)}</option>
          ))}
        </select>
        <select
          value={impactFilter}
          onChange={(e) => setImpactFilter(e.target.value as typeof impactFilter)}
          className="input !h-8 !w-auto !px-2"
        >
          <option value="all">{isZh ? '全部影响' : 'All impact'}</option>
          <option value="High">{isZh ? '高' : 'High'}</option>
          <option value="Medium">{isZh ? '中' : 'Medium'}</option>
          <option value="Low">{isZh ? '低' : 'Low'}</option>
        </select>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={isZh ? '搜寻事件...' : 'Search events...'}
          className="input !h-8 !w-40 !px-2"
        />
      </div>

      {isLoading ? (
        <div className="card p-8 text-center text-gf-muted">{t.loading}</div>
      ) : upcoming.length === 0 && ended.length === 0 ? (
        <div className="card p-8 text-center text-gf-muted">{t.noData}</div>
      ) : (
        <>
          {/* Tabs: upcoming | ended on one row */}
          <div className="mb-4 grid grid-cols-2 gap-2">
            <button
              onClick={() => setTab('upcoming')}
              className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                tab === 'upcoming'
                  ? 'border-gf-accent bg-gf-accent/15 text-gf-accent'
                  : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text'
              }`}
            >
              <span className="text-base leading-none">📅</span>
              <span>{t.upcoming}</span>
              <span className="rounded-full bg-gf-surface2 px-1.5 text-xs text-gf-muted">{upcoming.length}</span>
            </button>
            <button
              onClick={() => setTab('ended')}
              className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                tab === 'ended'
                  ? 'border-gf-accent bg-gf-accent/15 text-gf-accent'
                  : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text'
              }`}
            >
              <span className="text-base leading-none">✅</span>
              <span>{t.ended}</span>
              <span className="rounded-full bg-gf-surface2 px-1.5 text-xs text-gf-muted">{ended.length}</span>
            </button>
          </div>

          {/* Active tab content */}
          {tab === 'upcoming' && upcoming.length > 0 && (
            <div>
              {renderTable(upcoming, false)}
              {renderCards(upcoming, false)}
            </div>
          )}
          {tab === 'ended' && ended.length > 0 && (
            <div>
              {renderTable(ended, true)}
              {renderCards(ended, true)}
            </div>
          )}

          <div className="mt-2 text-right text-[11px] text-gf-muted">{t.source}</div>
        </>
      )}
    </div>
  );
}
